import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// Services & Projects — Profitability Page
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DollarSign, Target, Users, Activity, LineChart, Download,
  TrendingUp, FolderKanban, BarChart3
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { formatINR } from '@/shared/utils/currency';
import { useProfitability, useProjects, useCampaigns, useTimesheets, useExpenseAllocations, useInvoices, useJournalEntries } from '../hooks/useServicesProjectsApi';
import { useParties } from '@/features/accounts/hooks/useParties';
import { useUserOptions } from '@/shared/hooks/useUserOptions';

const PROFITABILITY_PERIODS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'project_lifetime', label: 'Project Lifetime' },
];

function getMarginColor(marginPct: number) {
  if (marginPct >= 30) return 'text-emerald-600';
  if (marginPct >= 15) return 'text-amber-600';
  if (marginPct >= 0) return 'text-blue-600';
  return 'text-red-600';
}

export function ProfitabilityPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'projects' | 'campaigns' | 'employees' | 'analytics'>('dashboard');
  const [period, setPeriod] = useState<'monthly' | 'quarterly' | 'yearly' | 'project_lifetime'>('monthly');
  const [search, _setSearch] = useState('');
  const [projectFilter, _setProjectFilter] = useState('');
  const [clientFilter, _setClientFilter] = useState('');
  const [managerFilter, _setManagerFilter] = useState('');

  const { data: _rawProfitability = [], isLoading: profitabilityLoading } = useProfitability();
  const { data: rawProjects = [] } = useProjects({ status: 'active' });
  const { data: rawCampaigns = [] } = useCampaigns({ status: 'active' });
  const { data: rawInvoices = [] } = useInvoices({});
  const { data: rawTimesheets = [] } = useTimesheets({});
  const { data: rawExpenses = [] } = useExpenseAllocations({});
  const { data: rawJournalEntries = [] } = useJournalEntries({});
  const { data: rawClients = [] } = useParties();
  const { data: rawUsers = [] } = useUserOptions();

  const projects = useMemo(() => Array.isArray(rawProjects) ? rawProjects : [], [rawProjects]);
  const campaigns = useMemo(() => Array.isArray(rawCampaigns) ? rawCampaigns : [], [rawCampaigns]);
  const invoices = useMemo(() => Array.isArray(rawInvoices) ? rawInvoices : [], [rawInvoices]);
  const timesheets = useMemo(() => Array.isArray(rawTimesheets) ? rawTimesheets : [], [rawTimesheets]);
  const expenses = useMemo(() => Array.isArray(rawExpenses) ? rawExpenses : [], [rawExpenses]);
  const journalEntries = useMemo(() => Array.isArray(rawJournalEntries) ? rawJournalEntries : [], [rawJournalEntries]);
  const clients = useMemo(() => Array.isArray(rawClients) ? rawClients : [], [rawClients]);
  const users = useMemo(() => Array.isArray(rawUsers) ? rawUsers : [], [rawUsers]);

  void journalEntries; void clients;

  // Filter data based on period
  const getPeriodBounds = (periodType: string) => {
    const now = new Date();
    let from: Date;
    const to = new Date();
    
    switch (periodType) {
      case 'daily':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'weekly':
        from = new Date(now);
        from.setDate(now.getDate() - now.getDay());
        break;
      case 'monthly':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarterly':
        from = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      case 'yearly':
        from = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        from = new Date(0);
    }
    return { from, to };
  };

  const { from: periodFrom, to: periodTo } = getPeriodBounds(period);
  void periodFrom; void periodTo;

  // Filter data within period
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (search && !p.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (projectFilter && p._id !== projectFilter) return false;
      if (clientFilter && p.clientId !== clientFilter) return false;
      if (managerFilter && p.projectManagerId !== managerFilter) return false;
      return true;
    });
  }, [projects, search, projectFilter, clientFilter, managerFilter]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (clientFilter && c.clientId !== clientFilter) return false;
      return true;
    });
  }, [campaigns, search, clientFilter]);

  // Real monthly revenue trend from actual invoices (amountMinor grouped by
  // invoiceDate's month) — this used to be 6 hardcoded bars with a fake,
  // always-increasing value. Cancelled invoices excluded, same as
  // projectProfitability's revenue calc below.
  const monthlyTrend = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - (5 - i));
      return { key: d.toISOString().slice(0, 7), label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), revenueMinor: 0 };
    });
    const byKey = new Map(months.map((m) => [m.key, m]));
    for (const inv of invoices) {
      if (inv.status === 'cancelled' || !inv.invoiceDate) continue;
      const key = String(inv.invoiceDate).slice(0, 7);
      const month = byKey.get(key);
      if (month) month.revenueMinor += inv.amountMinor || 0;
    }
    return months;
  }, [invoices]);
  const monthlyTrendMax = Math.max(1, ...monthlyTrend.map((m) => m.revenueMinor));
  const hasTrendData = monthlyTrend.some((m) => m.revenueMinor > 0);

  // Compute project profitability
  const projectProfitability = useMemo(() => {
    return filteredProjects.map(p => {
      // Revenue from invoices
      const projectInvoices = invoices.filter(i => i.projectId === p._id && i.status !== 'cancelled');
      const revenue = projectInvoices.reduce((sum, i) => sum + (i.amountMinor || 0), 0);
      const paid = projectInvoices.reduce((sum, i) => sum + (i.paidAmountMinor || 0), 0);
      
      // Cost from timesheets (employee cost)
      const projectTimesheets = timesheets.filter(t => t.projectId === p._id);
      const laborCost = projectTimesheets.reduce((sum, t) => sum + Math.round((t.minutes || 0) / 60 * (t.costRateMinor || 0)), 0);
      
      // Expenses
      const projectExpenses = expenses.filter(e => e.projectId === p._id);
      const expenseCost = projectExpenses.reduce((sum, e) => sum + (e.amountMinor || 0), 0);
      
      // Campaign costs (media spend)
      const projectCampaigns = campaigns.filter(c => c.projectId === p._id);
      const campaignCost = projectCampaigns.reduce((sum, c) => sum + (c.spendMinor || 0), 0);
      const campaignRevenue = projectCampaigns.reduce((sum, c) => sum + (c.revenueMinor || 0), 0);
      
      const totalRevenue = revenue + campaignRevenue;
      const totalCost = laborCost + expenseCost + campaignCost;
      const grossProfit = totalRevenue - totalCost;
      const marginPct = totalRevenue > 0 ? (grossProfit / totalRevenue * 100) : 0;
      
      // Billable hours & utilization
      const billableMinutes = projectTimesheets.filter(t => t.billable).reduce((sum, t) => sum + (t.minutes || 0), 0);
      const totalMinutes = projectTimesheets.reduce((sum, t) => sum + (t.minutes || 0), 0);
      const billableHours = billableMinutes / 60;
      const utilizationPct = totalMinutes > 0 ? (billableMinutes / totalMinutes * 100) : 0;
      
      // Budget vs Actual
      const budget = p.budgetMinor || 0;
      const budgetVariance = budget > 0 ? ((totalCost - budget) / budget * 100) : 0;
      
      return {
        project: p,
        revenue: totalRevenue,
        paid,
        outstanding: revenue - paid,
        laborCost,
        expenseCost,
        campaignCost,
        campaignRevenue,
        totalCost,
        grossProfit,
        marginPct,
        billableHours,
        utilizationPct,
        budget,
        budgetVariance,
        invoiceCount: projectInvoices.length,
        timesheetCount: projectTimesheets.length,
      };
    });
  }, [filteredProjects, invoices, timesheets, expenses, campaigns]);

  const campaignProfitability = useMemo(() => {
    return filteredCampaigns.map(c => {
      const revenue = c.revenueMinor || 0;
      const spend = c.spendMinor || 0;
      const agencyFee = c.agencyFeeMinor || 0;
      const totalCost = spend + agencyFee;
      const grossProfit = revenue - totalCost;
      const marginPct = revenue > 0 ? (grossProfit / revenue * 100) : 0;
      const roas = spend > 0 ? (revenue / spend) : 0;
      const cpa = c.conversions > 0 ? (spend / c.conversions) : 0;
      
      return {
        campaign: c,
        revenue,
        spend,
        agencyFee,
        totalCost,
        grossProfit,
        marginPct,
        roas,
        cpa,
        conversions: c.conversions || 0,
        impressions: c.impressions || 0,
        clicks: c.clicks || 0,
      };
    });
  }, [filteredCampaigns]);

  // Employee profitability
  const employeeProfitability = useMemo(() => {
    const empMap: Record<string, { 
      user: any; 
      billableHours: number; 
      nonBillableHours: number; 
      revenue: number; 
      cost: number; 
      projects: Set<string>;
    }> = {};
    
    timesheets.forEach(t => {
      if (!empMap[t.userId]) {
        empMap[t.userId] = { 
          user: users.find(u => u.id === t.userId), 
          billableHours: 0, 
          nonBillableHours: 0, 
          revenue: 0, 
          cost: 0, 
          projects: new Set() 
        };
      }
      const hours = (t.minutes || 0) / 60;
      if (t.billable) {
        empMap[t.userId].billableHours += hours;
        empMap[t.userId].revenue += hours * (t.billingRateMinor || 0);
      } else {
        empMap[t.userId].nonBillableHours += hours;
      }
      empMap[t.userId].cost += hours * (t.costRateMinor || 0);
      if (t.projectId) empMap[t.userId].projects.add(t.projectId);
    });
    
    return Object.values(empMap).map(e => {
      const totalHours = e.billableHours + e.nonBillableHours;
      return {
        ...e,
        totalHours,
        utilizationPct: totalHours > 0 ? (e.billableHours / totalHours * 100) : 0,
        margin: e.revenue - e.cost,
        marginPct: e.revenue > 0 ? ((e.revenue - e.cost) / e.revenue * 100) : 0,
        projectCount: e.projects.size,
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [timesheets, users]);

  // Overall metrics
  const overallMetrics = useMemo(() => {
    const totalRevenue = projectProfitability.reduce((sum, p) => sum + p.revenue, 0) + 
                         campaignProfitability.reduce((sum, c) => sum + c.revenue, 0);
    const totalCost = projectProfitability.reduce((sum, p) => sum + p.totalCost, 0) + 
                      campaignProfitability.reduce((sum, c) => sum + c.totalCost, 0);
    const totalGrossProfit = totalRevenue - totalCost;
    const overallMargin = totalRevenue > 0 ? (totalGrossProfit / totalRevenue * 100) : 0;
    const totalBillableHours = projectProfitability.reduce((sum, p) => sum + p.billableHours, 0);
    const avgUtilization = employeeProfitability.length > 0 
      ? employeeProfitability.reduce((sum, e) => sum + e.utilizationPct, 0) / employeeProfitability.length 
      : 0;
    
    return {
      totalRevenue,
      totalCost,
      grossProfit: totalGrossProfit,
      marginPct: overallMargin,
      totalBillableHours,
      avgUtilization,
      projectCount: projectProfitability.length,
      campaignCount: campaignProfitability.length,
      employeeCount: employeeProfitability.length,
    };
  }, [projectProfitability, campaignProfitability, employeeProfitability]);

  const isLoading = profitabilityLoading; void isLoading;

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={BarChart3}
        eyebrow="Projects & Services"
        title="Profitability"
        description={<>Real-time margin analysis across projects, campaigns, and employees</>}
        actions={
          <><div className="flex gap-2 items-center">
                  <Select value={period} onChange={e => setPeriod(e.target.value as any)} className="w-40" options={PROFITABILITY_PERIODS} />
                  <Button variant="outline" onClick={() => setActiveTab('dashboard')}>
                    <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('projects')}>
                    <FolderKanban className="w-4 h-4 mr-2" /> Projects
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('campaigns')}>
                    <Target className="w-4 h-4 mr-2" /> Campaigns
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('employees')}>
                    <Users className="w-4 h-4 mr-2" /> Employees
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('analytics')}>
                    <LineChart className="w-4 h-4 mr-2" /> Analytics
                  </Button>
                  <Button onClick={() => toast.success('Export functionality coming soon')}>
                    <Download className="w-4 h-4 mr-2" /> Export
                  </Button>
                </div></>
        }
      />

      {/* Overview Dashboard */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold">{formatINR(overallMetrics.totalRevenue)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Cost</p>
                    <p className="text-2xl font-bold text-red-600">{formatINR(overallMetrics.totalCost)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-red-500" style={{ transform: 'rotate(180deg)' }} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Gross Profit</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatINR(overallMetrics.grossProfit)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Margin</p>
                    <p className="text-2xl font-bold" style={{ color: getMarginColor(overallMetrics.marginPct) }}>
                      {overallMetrics.marginPct.toFixed(1)}%
                    </p>
                  </div>
                  <Target className="w-8 h-8" style={{ color: getMarginColor(overallMetrics.marginPct) }} />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Billable Hours</p>
                    <p className="text-2xl font-bold">{overallMetrics.totalBillableHours.toFixed(1)}</p>
                  </div>
                  <Activity className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Utilization</p>
                    <p className="text-2xl font-bold">{overallMetrics.avgUtilization.toFixed(1)}%</p>
                  </div>
                  <Users className="w-8 h-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts & Tables */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Project Profitability Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Project Profitability</CardTitle>
                  <Badge className="bg-blue-100 text-blue-800">{projectProfitability.length} projects</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {projectProfitability.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No projects</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Project</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead>Margin</TableHead>
                          <TableHead>Utilization</TableHead>
                          <TableHead>Budget Var.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {projectProfitability
                          .sort((a, b) => b.grossProfit - a.grossProfit)
                          .slice(0, 10)
                          .map((p) => (
                            <TableRow key={p.project._id}>
                              <TableCell className="font-medium">{p.project.name}</TableCell>
                              <TableCell className="text-right font-mono">{formatINR(p.revenue)}</TableCell>
                              <TableCell className="text-right font-mono text-red-600">{formatINR(p.totalCost)}</TableCell>
                              <TableCell className="text-right font-mono font-bold text-emerald-600">{formatINR(p.grossProfit)}</TableCell>
                              <TableCell>
                                <span style={{ color: getMarginColor(p.marginPct) }} className="font-medium">
                                  {p.marginPct.toFixed(1)}%
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                                    <div className="h-full bg-indigo-500 rounded" style={{ width: `${Math.min(p.utilizationPct, 100)}%` }} />
                                  </div>
                                  <span className="text-sm">{p.utilizationPct.toFixed(0)}%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className={p.budgetVariance > 10 ? 'text-red-600' : p.budgetVariance > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                                  {p.budget > 0 ? (p.budgetVariance > 0 ? '+' : '') + p.budgetVariance.toFixed(1) + '%' : '—'}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                </div>
              )}
            </CardContent>
          </Card>

            {/* Campaign ROI Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Campaign ROI</CardTitle>
                  <Badge className="bg-purple-100 text-purple-800">{campaignProfitability.length} campaigns</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {campaignProfitability.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No campaigns</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Campaign</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Spend</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead>ROAS</TableHead>
                          <TableHead>CPA</TableHead>
                          <TableHead>Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignProfitability
                          .sort((a, b) => b.grossProfit - a.grossProfit)
                          .map((c) => (
                            <TableRow key={c.campaign._id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{c.campaign.name}</p>
                                  <p className="text-xs text-gray-500 capitalize">{c.campaign.platform?.replace('_', ' ')}</p>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">{formatINR(c.revenue)}</TableCell>
                              <TableCell className="text-right font-mono text-red-600">{formatINR(c.spend)}</TableCell>
                              <TableCell className="text-right font-mono font-bold text-emerald-600">{formatINR(c.grossProfit)}</TableCell>
                              <TableCell className="font-mono font-bold">{c.roas.toFixed(2)}x</TableCell>
                              <TableCell className="font-mono">{formatINR(c.cpa)}</TableCell>
                              <TableCell>
                                <span style={{ color: getMarginColor(c.marginPct) }} className="font-medium">
                                  {c.marginPct.toFixed(1)}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
              )}
            </CardContent>
          </Card>

            {/* Employee Profitability */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Employee Profitability</CardTitle>
                  <Badge className="bg-indigo-100 text-indigo-800">{employeeProfitability.length} employees</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {employeeProfitability.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No timesheet data</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Billable Hrs</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Margin</TableHead>
                          <TableHead>Utilization</TableHead>
                          <TableHead>Projects</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employeeProfitability.map((e) => (
                          <TableRow key={e.user?._id || e.user?.id}>
                            <TableCell className="font-medium">{e.user?.displayName || e.user?.name || 'Unknown'}</TableCell>
                            <TableCell className="text-right font-mono">{e.billableHours.toFixed(1)}</TableCell>
                            <TableCell className="text-right font-mono">{formatINR(e.revenue)}</TableCell>
                            <TableCell className="text-right font-mono text-red-600">{formatINR(e.cost)}</TableCell>
                            <TableCell className="text-right font-mono font-bold" style={{ color: getMarginColor(e.marginPct) }}>
                              {formatINR(e.margin)} ({e.marginPct.toFixed(1)}%)
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                                  <div className="h-full bg-indigo-500 rounded" style={{ width: `${Math.min(e.utilizationPct, 100)}%` }} />
                                </div>
                                <span className="text-sm">{e.utilizationPct.toFixed(0)}%</span>
                              </div>
                            </TableCell>
                            <TableCell>{e.projectCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Projects Profitability</CardTitle>
              <div className="flex gap-2">
                <Select value={period} onChange={e => setPeriod(e.target.value as any)} className="w-36" options={PROFITABILITY_PERIODS} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Util.</TableHead>
                    <TableHead>Budget Var.</TableHead>
                    <TableHead>Invoices</TableHead>
                    <TableHead>Timesheets</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectProfitability.map((p, idx) => (
                    <TableRow key={p.project?._id || idx}>
                      <TableCell className="font-medium">{p.project?.name || '—'}</TableCell>
                      <TableCell>{p.project.clientName || '—'}</TableCell>
                      <TableCell className="text-right font-mono">{formatINR(p.revenue)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{formatINR(p.totalCost)}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-600">{formatINR(p.grossProfit)}</TableCell>
                      <TableCell>
                        <span style={{ color: getMarginColor(p.marginPct) }} className="font-medium">
                          {p.marginPct.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{p.utilizationPct.toFixed(0)}%</TableCell>
                      <TableCell className="text-right">
                        <span className={p.budgetVariance > 10 ? 'text-red-600' : p.budgetVariance > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                          {p.budget > 0 ? (p.budgetVariance > 0 ? '+' : '') + p.budgetVariance.toFixed(1) + '%' : '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{p.invoiceCount}</TableCell>
                      <TableCell className="text-right">{p.timesheetCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <Card>
          <CardHeader><CardTitle>All Campaigns ROI</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead>ROAS</TableHead>
                    <TableHead>CPA</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Conv.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignProfitability.map((c, idx) => (
                    <TableRow key={c.campaign?._id || idx}>
                      <TableCell className="font-medium">{c.campaign?.name || '—'}</TableCell>
                      <TableCell><Badge variant="secondary">{c.campaign?.platform?.replace('_', ' ').toUpperCase() || '—'}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{formatINR(c.revenue)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{formatINR(c.spend)}</TableCell>
                      <TableCell className="text-right font-mono text-purple-600">{formatINR(c.agencyFee)}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-600">{formatINR(c.grossProfit)}</TableCell>
                      <TableCell className="font-mono font-bold">{c.roas.toFixed(2)}x</TableCell>
                      <TableCell className="font-mono">{formatINR(c.cpa)}</TableCell>
                      <TableCell>
                        <span style={{ color: getMarginColor(c.marginPct) }} className="font-medium">
                          {c.marginPct.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{c.conversions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <Card>
          <CardHeader><CardTitle>Employee Profitability & Utilization</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Billable Hrs</TableHead>
                    <TableHead className="text-right">Non-Billable</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Projects</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employeeProfitability.map((e) => (
                    <TableRow key={e.user?._id || e.user?.id}>
                      <TableCell className="font-medium">{e.user?.displayName || e.user?.name || 'Unknown'}</TableCell>
                      <TableCell className="text-right font-mono">{e.billableHours.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono text-gray-500">{e.nonBillableHours.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono">{formatINR(e.revenue)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{formatINR(e.cost)}</TableCell>
                      <TableCell className="text-right font-mono font-bold" style={{ color: getMarginColor(e.marginPct) }}>
                        {formatINR(e.margin)} ({e.marginPct.toFixed(1)}%)
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded" style={{ width: `${Math.min(e.utilizationPct, 100)}%` }} />
                          </div>
                          <span className="text-sm">{e.utilizationPct.toFixed(0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{e.projectCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Revenue Waterfall */}
          <Card>
            <CardHeader><CardTitle>Revenue Waterfall</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                  <span>Total Contract Value</span>
                  <span className="font-bold">{formatINR(projectProfitability.reduce((s, p) => s + (p.project?.budgetMinor || 0), 0))}</span>
                </div>
                <div className="flex justify-between p-3 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 rounded-r">
                  <span>+ Invoiced Revenue</span>
                  <span className="font-bold text-blue-600">+{formatINR(overallMetrics.totalRevenue)}</span>
                </div>
                <div className="flex justify-between p-3 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20 rounded-r">
                  <span>- Direct Costs</span>
                  <span className="font-bold text-red-600">-{formatINR(overallMetrics.totalCost)}</span>
                </div>
                <div className="flex justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded font-bold text-emerald-600">
                  <span>Gross Profit</span>
                  <span>{formatINR(overallMetrics.grossProfit)} ({overallMetrics.marginPct.toFixed(1)}%)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card>
            <CardHeader><CardTitle>Monthly Revenue Trend</CardTitle></CardHeader>
            <CardContent>
              {hasTrendData ? (
                <div className="h-64 space-y-2">
                  {monthlyTrend.map((m) => (
                    <div key={m.key} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-gray-500">{m.label}</span>
                      <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded relative overflow-hidden">
                        <div className="absolute top-0 bottom-0 left-0 bg-emerald-500 rounded" style={{ width: `${(m.revenueMinor / monthlyTrendMax) * 90}%` }} />
                      </div>
                      <span className="w-24 text-sm text-right font-mono text-emerald-600">{formatINR(m.revenueMinor)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-24 flex items-center justify-center text-sm text-gray-400">
                  No invoiced revenue in the last 6 months yet.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top/Bottom Performers */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Top 5 Most Profitable Projects</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {projectProfitability
                    .filter(p => p.grossProfit > 0)
                    .sort((a, b) => b.grossProfit - a.grossProfit)
                    .slice(0, 5)
                    .map((p, i) => (
                      <div key={p.project._id} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-bold text-emerald-600">{i + 1}</span>
                          <div>
                            <p className="font-medium">{p.project.name}</p>
                            <p className="text-xs text-gray-500">{p.project.clientName || '—'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatINR(p.grossProfit)}</p>
                          <p className="text-xs text-gray-500">{p.marginPct.toFixed(1)}% margin</p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Projects Needing Attention</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {projectProfitability
                    .filter(p => p.grossProfit < 0 || p.marginPct < 10 || p.budgetVariance > 10)
                    .sort((a, b) => a.grossProfit - b.grossProfit)
                    .slice(0, 5)
                    .map((p) => (
                      <div key={p.project._id} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded border-l-4 border-red-500">
                        <div>
                          <p className="font-medium text-red-600">{p.project.name}</p>
                          <p className="text-xs text-gray-500">{p.project.clientName || '—'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">{formatINR(p.grossProfit)}</p>
                          <p className="text-xs text-gray-500">
                            {p.marginPct < 10 ? `Low margin: ${p.marginPct.toFixed(1)}%` : ''}
                            {p.budgetVariance > 10 ? `Over budget: +${p.budgetVariance.toFixed(1)}%` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
